/**
 * `ChatTable` — wraps every GFM `<table>` in chat assistant prose
 * with a title row that carries copy + download icon buttons (PLT-4835).
 *
 * Pieces:
 *   1. `remarkTableTitle` (sibling file) hoists the bold paragraph
 *      above a table into `data-table-title` on the `<table>` itself.
 *   2. This component reads that attribute via the `data-table-title`
 *      prop the markdown renderer forwards, falls back to `"Table"`
 *      if absent, and renders the title row on top of the existing
 *      horizontally-scrolling table wrapper.
 *   3. The handlers serialise the rendered table's DOM (via `ref`) so
 *      a user copies / downloads *exactly what they see* — including
 *      tone-coloured cell content rendered by `remark-table-sign-color`
 *      and any inline tone badges.
 *
 * Graceful failure
 * ----------------
 * LLM-generated tables are messy: the model can emit a table with no
 * `<thead>`, a single row, duplicate headers, missing cells, or stop
 * streaming mid-row. The component never throws on any of these — the
 * worst case is an actions row that copies a smaller set of cells, or
 * an .xlsx with auto-named columns. Concrete guards:
 *   - Title absent / empty → fallback to `"Table"`.
 *   - Table ref unset on click (extremely unlikely) → silently no-op.
 *   - No `<thead>` → fall back to first `<tbody>` row as the header.
 *   - No rows at all → copy / download just the headers; if there are
 *     no headers either, copy a single empty string and skip the
 *     download (we still don't crash).
 *   - Duplicate header labels → de-duplicated with ` (2)`, ` (3)` …
 *     suffixes so `xlsx.utils.json_to_sheet` doesn't silently collapse
 *     them into one column.
 *   - Handler throws (clipboard permission denied on some embedded
 *     contexts, xlsx writer failing on a pathological input) → caught
 *     and logged to the console; the user just sees the action do
 *     nothing rather than the whole assistant message crashing.
 *
 * The title row sits *outside* the horizontally-scrolling
 * `.upright-chat-table-wrapper` so the title and action icons stay
 * pinned while wide tables scroll underneath them.
 */
import { useCallback, useRef, type ReactNode } from 'react';
import copy from 'copy-to-clipboard';

import { downloadAsXlsx } from '@upright/ui/common/generic/content/tables/spreadsheet-to-excel';

import {
  FALLBACK_TITLE,
  deduplicateHeaders,
  parseTableElement,
  sanitizeSheetName,
  slugifyTitle,
  tableToSheetRows,
  tableToTsv,
} from './chat-table-helpers';

/**
 * Inline SVG icons. Streamline-core silhouettes at the same 1.5-stroke
 * weight as `text-file-streamline-core.tsx`. Inlined (rather than added
 * to `src/ui/icons/`) so the file is self-contained — the two icons
 * are only used by this component.
 */
function CopyIcon(props: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={props.className}
    >
      <rect x={6} y={6} width={10} height={11} rx={1.5} />
      <path d="M12 6V4.5A1.5 1.5 0 0 0 10.5 3H4.5A1.5 1.5 0 0 0 3 4.5v8A1.5 1.5 0 0 0 4.5 14H6" />
    </svg>
  );
}

function DownloadIcon(props: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={props.className}
    >
      <path d="M10 3v10" />
      <path d="m5.5 8.5 4.5 4.5 4.5-4.5" />
      <path d="M3 16h14" />
    </svg>
  );
}

/**
 * `components.table` override for `react-markdown`. React passes any
 * `data-*` hProperties through as kebab-cased React props, so the
 * `data-table-title` attribute stamped by `remarkTableTitle` arrives
 * at `props['data-table-title']`.
 *
 * We intentionally don't pull the title through a stricter typed prop —
 * react-markdown's component-override types declare `rest` as
 * `Record<string, unknown>` and indexing the rest bag with a literal
 * keeps us decoupled from the upstream type rename history.
 */
export function ChatTable({ children, ...rest }: { children?: ReactNode; [key: string]: unknown }) {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const rawTitle = typeof rest['data-table-title'] === 'string' ? rest['data-table-title'] : '';
  // When the LLM forgot to emit a title row above the table we previously
  // rendered the title row with the fallback word "Table", which looked
  // off. Drop the whole title row in that case and float the copy/download
  // icons over the top-right corner of the data grid instead. The
  // FALLBACK_TITLE constant still drives the downloaded sheet name + xlsx
  // filename, so unnamed tables remain saveable.
  const hasTitle = rawTitle.trim().length > 0;
  const title = hasTitle ? rawTitle.trim() : FALLBACK_TITLE;

  // `data-table-title` is consumed; don't forward it to the DOM —
  // React would warn about a custom attribute on the underlying
  // `<table>` if it stayed in `tableProps`.
  const tableProps: Record<string, unknown> = { ...rest };
  delete tableProps['data-table-title'];

  const onCopy = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    try {
      const parsed = parseTableElement(table);
      const tsv = tableToTsv(parsed);
      copy(tsv);
    } catch (err) {
      // Defensive — copy-to-clipboard can throw in sandboxed iframes
      // where clipboard access is blocked. The whole chat message
      // shouldn't crash because the user clicked an icon that didn't
      // work; log and move on.
      // eslint-disable-next-line no-console
      console.warn('[ChatTable] copy failed', err);
    }
  }, []);

  const onDownload = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    try {
      const parsed = parseTableElement(table);
      const sheetRows = tableToSheetRows(parsed);
      // Skip the download when the table is entirely empty — there's
      // nothing meaningful to write to .xlsx and the file would be
      // confusing rather than useful.
      if (sheetRows.length === 0 && parsed.headers.length === 0) return;
      const sheetName = sanitizeSheetName(title);
      const filename = `upright-chat-${slugifyTitle(title)}`;
      // `json_to_sheet` needs at least one row to infer columns. If the
      // user is downloading a header-only table, synthesise an empty
      // row so the headers still appear.
      const dataRows =
        sheetRows.length > 0 ? sheetRows : [Object.fromEntries(deduplicateHeaders(parsed.headers).map(h => [h, '']))];
      downloadAsXlsx({ [sheetName]: dataRows }, filename);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ChatTable] download failed', err);
    }
  }, [title]);

  const actionButtons = (
    <>
      <button
        type="button"
        className="upright-chat-table-action"
        title="Copy as TSV"
        aria-label="Copy table as TSV"
        onClick={onCopy}
      >
        <CopyIcon />
      </button>
      <button
        type="button"
        className="upright-chat-table-action"
        title="Download as Excel (.xlsx)"
        aria-label="Download table as Excel"
        onClick={onDownload}
      >
        <DownloadIcon />
      </button>
    </>
  );

  return (
    <div className={`upright-chat-table-card${hasTitle ? '' : ' upright-chat-table-card--no-title'}`}>
      {hasTitle && (
        <div className="upright-chat-table-title-row">
          <span className="upright-chat-table-title">{title}</span>
          <div className="upright-chat-table-actions">{actionButtons}</div>
        </div>
      )}
      <div className="upright-chat-table-wrapper">
        <table ref={tableRef} {...tableProps}>
          {children}
        </table>
      </div>
      {!hasTitle && (
        <div className="upright-chat-table-actions upright-chat-table-actions--floating">{actionButtons}</div>
      )}
    </div>
  );
}
